import { useState, useRef, useCallback, useEffect } from "react";
import { database } from "../../../firebase";
import {
    ref,
    set,
    serverTimestamp,
    onValue,
    off,
} from "firebase/database";
import { Html5Qrcode } from "html5-qrcode";
import { loadAllowedDomains, isAllowedScannedUrl } from "../utils/domainValidators";
import { getLocation } from "../utils/locationUtils";

const useScanner = ({ user, onStopScan, setUniqueKey, setUserID }) => {
    const [message, setMessage] = useState("");
    const [instructions, setInstructions] = useState("");
    const [showRetry, setShowRetry] = useState(false);

    const qrReaderRef = useRef(null);
    const isProcessingRef = useRef(false);

    const allowedList = loadAllowedDomains();

    /* ---------- Scanner Cleanup ---------- */

    const stopAndClearScanner = async () => {
        if (!qrReaderRef.current) return;

        try {
            await qrReaderRef.current.stop?.();
            await qrReaderRef.current.clear?.();
        } catch (err) {
            console.warn("Error stopping scanner:", err);
        } finally {
            qrReaderRef.current = null;
        }
    };

    /* ---------- Duplicate Views ---------- */

    const showQRDuplicateView = async () => {
        await stopAndClearScanner();
        setMessage("QR Code Already Scanned");
        setInstructions(
            "This QR code has already been scanned.\n\nPlease scan a different QR code."
        );
        setShowRetry(true);
    };



    /* ---------- Save Temp Scan & Wait ---------- */

    const saveTempScanAndWait = async (
        decodedText,
        latitude,
        longitude
    ) => {
        const userPath =
            user?.email?.replaceAll(".", "-") || "unknown-user";

        const scanID =
            decodedText.replaceAll(/[^a-zA-Z0-9_-]/g, "-");

        const tempRef = ref(
            database,
            `data/temp/${userPath}/${scanID}`
        );

        const statusRef = ref(
            database,
            `data/temp/${userPath}/${scanID}`
        );

        setMessage("Verifying...");
        setInstructions("");
        setShowRetry(false);

        await set(tempRef, {
            qrcodeData: decodedText,
            coordinates: { x: latitude, y: longitude },
            createTimestamp: serverTimestamp(),
            tempData: false,
        });

        return new Promise((resolve) => {
            const listener = onValue(statusRef, async (snap) => {
                const val = snap.val();
                if (!val) return;

                const { redundant } = val;

                if (redundant === undefined) return;

                if (redundant === true) {
                    off(statusRef, "value", listener);
                    await showQRDuplicateView();
                    resolve();
                    return;
                }



                if (redundant === false) {
                    off(statusRef, "value", listener);
                    setUniqueKey?.(scanID);
                    setUserID?.(userPath);
                    onStopScan?.();
                    resolve();
                }
            });
        });
    };

    /* ---------- QR Handler ---------- */

    const handleDecodedText = async (decodedText) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            if (!isAllowedScannedUrl(decodedText, allowedList)) {
                await stopAndClearScanner();
                setMessage("Invalid QR Code");
                setInstructions("Please scan a valid QR code.");
                setShowRetry(true);
                return;
            }

            // Stronger vibration: 200ms single pulse
            if (navigator.vibrate) {
                try {
                    navigator.vibrate(200);
                } catch (e) {
                    console.error("Vibration failed", e);
                }
            }

            const position = await getLocation();
            await stopAndClearScanner();

            await saveTempScanAndWait(
                decodedText,
                position.coords.latitude,
                position.coords.longitude
            );
        } catch (err) {
            await stopAndClearScanner();

            // ✅ GPS OFF / LOCATION UNAVAILABLE
            if (err?.code === 2) {
                setMessage("Location Permission Blocked");
                setInstructions(
                    "1. Tap the 🔒 Lock icon (or settings icon) in your browser address bar.\n" +
                    "2. Click \"Permissions\" or \"Location\".\n" +
                    "3. Select \"Reset Permission\" or \"Allow\".\n" +
                    "4. Then click the button below."
                );
                setShowRetry(true);
                return;
            }

            console.error("Scanner error:", err);
            setMessage("Camera / Location Permission Blocked");
            setInstructions(
                "1. Tap the 🔒 Lock icon (or settings icon) in your browser address bar.\n" +
                "2. Click \"Permissions\" or \"Location\".\n" +
                "3. Select \"Reset Permission\" or \"Allow\".\n" +
                "4. Then click the button below.");
            setShowRetry(true);
        } finally {
            isProcessingRef.current = false;
        }
    };

    /* ---------- Start Scanner ---------- */

    const startScanner = useCallback(async () => {
        try {
            setMessage("");
            setInstructions("");
            setShowRetry(false);
            isProcessingRef.current = false;

            await stopAndClearScanner();

            const qrReader = new Html5Qrcode("reader");
            qrReaderRef.current = qrReader;

            await qrReader.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 400, height: 400 } },
                handleDecodedText
            );
        } catch (err) {
            console.error("Scanner start error:", err);
            setMessage("Camera / Location Permission Blocked");
            setInstructions(
                "1. Tap the 🔒 Lock icon (or settings icon) in your browser address bar.\n" +
                "2. Click \"Permissions\" or \"Location\".\n" +
                "3. Select \"Reset Permission\" or \"Allow\".\n" +
                "4. Then click the button below."
            );
            setShowRetry(true);
        }
    }, []);

    /* ---------- Lifecycle ---------- */

    useEffect(() => {
        startScanner();
        return () => stopAndClearScanner();
    }, [startScanner]);

    return {
        message,
        instructions,
        showRetry,
        startScanner,
    };
};

export default useScanner;
